import { useCallback } from 'react'
import { useStore } from '@xyflow/react'
import { toBounds } from '../utils/canvasAlignment'
import { markOffscreen, NO_OFFSCREEN, overlapsViewport, sameDirections, type OffscreenDirections } from '../utils/canvasOffscreen'
import { isZoomedOut } from '../utils/canvasViewport'

/**
 * Was ein Bild über die Lage der **Canvas-Elemente** zum **Ausschnitt** sagt.
 *
 * Zwei Auskünfte aus EINEM Durchgang, weil zwei Dinge daran hängen und beide
 * bei jeder Store-Änderung neu entschieden werden — beim Schieben also je
 * Bild. Zwei getrennte Selektoren wären zwei Schleifen über hundert Elemente,
 * und schlimmer: zwei Wahrheiten darüber, was „draußen" heißt.
 */
export interface OffscreenState {
  /** Für die **Randpfeile**: in welchen Richtungen etwas liegt. */
  directions: OffscreenDirections
  /**
   * Für die **Karte**: ob von IRGENDEINEM Element etwas zu sehen ist — ein
   * Zipfel genügt. Ist gar nichts zu sehen, hat man nichts, woran man sich
   * verorten könnte (ADR 0016).
   *
   * Bewusst eine andere Schwelle als die der Pfeile: Die fragen „kann ich es
   * lesen?", die Karte fragt „weiß ich, wo ich bin?".
   */
  anyVisible: boolean
  /**
   * Ob es überhaupt Elemente gibt. Ohne sie wäre `anyVisible` falsch und die
   * Karte käme — auf einem leeren Canvas, wo es nichts zu zeigen gibt.
   */
  hasElements: boolean
  /**
   * Ob der **Ausschnitt** ganz herausgezoomt ist. Als Boolean und nicht als
   * Zahl: Der Wert wechselt dann nur an der Grenze, während ein Zoomwert bei
   * jedem Rad-Anschlag ein neues Rendern auslöste.
   */
  zoomedOut: boolean
}

const NOTHING: OffscreenState = {
  directions: NO_OFFSCREEN,
  anyVisible: false,
  hasElements: false,
  zoomedOut: false,
}

/** Der Ausschnitt der Karte, wie React Flow ihn im Store führt. */
interface FlowSlice {
  transform: [number, number, number]
  width: number
  height: number
  nodeLookup: Map<string, {
    internals: { positionAbsolute: { x: number; y: number } }
    measured?: { width?: number; height?: number }
  }>
}

function sameState(a: OffscreenState, b: OffscreenState): boolean {
  return a.anyVisible === b.anyVisible &&
    a.hasElements === b.hasElements &&
    a.zoomedOut === b.zoomedOut &&
    sameDirections(a.directions, b.directions)
}

/**
 * Die Lage der Elemente zum Ausschnitt, aus React Flows Store.
 *
 * Der Vergleich (`sameState`) entscheidet, ob überhaupt neu gerendert wird:
 * Die Auswertung läuft bei JEDER Store-Änderung, die Werte wechseln aber nur
 * in dem Moment, in dem ein Element eine Kante überquert.
 */
export function useOffscreenState(): OffscreenState {
  const select = useCallback((state: FlowSlice): OffscreenState => {
    const [x, y, zoom] = state.transform
    if (!zoom || !state.width || !state.height) return NOTHING

    // Derselbe Weg wie `viewportBounds` in `CanvasFlow`: aus der Verschiebung
    // des Viewports und den Maßen der Pane.
    const viewport = toBounds({
      x: -x / zoom,
      y: -y / zoom,
      width: state.width / zoom,
      height: state.height / zoom,
    })

    // Ohne ein Objekt je Node: Dieser Selektor läuft je Bild (siehe
    // `markOffscreen`).
    const directions = { ...NO_OFFSCREEN }
    let anyVisible = false
    let hasElements = false
    for (const [, node] of state.nodeLookup) {
      const width = node.measured?.width
      const height = node.measured?.height
      // Ein noch ungemessener Node meldet 0 × 0. Ein Rechteck ohne Ausdehnung
      // läge rechnerisch immer irgendwo außerhalb und erzeugte einen Pfeil für
      // nichts. Dieselbe Falle wie bei `sizeOf` in `CanvasFlow`.
      if (!width || !height) continue
      hasElements = true
      const { x, y } = node.internals.positionAbsolute
      markOffscreen(directions, x, y, x + width, y + height, viewport)
      if (!anyVisible) anyVisible = overlapsViewport(x, y, x + width, y + height, viewport)
    }

    return { directions, anyVisible, hasElements, zoomedOut: isZoomedOut(zoom) }
  }, [])

  return useStore(select, sameState)
}
